import { ArchBaseService } from "@/common/api.service";

const API_BASE = "admin/onboarding";

const state = {
  submissions: [],
  summary: {},
  pagination: { page: 1, limit: 20, total_items: 0, total_pages: 0 },
  currentSubmission: null,
  rejectionCategories: [],
  auditLog: [],
  auditLogPagination: { page: 1, limit: 50, total_items: 0, total_pages: 0 },
  availableProducts: [],
  availableCountries: [],
  isLoading: false,
  error: null,
};

const getters = {
  submissions: (state) => state.submissions,
  summary: (state) => state.summary,
  pagination: (state) => state.pagination,
  currentSubmission: (state) => state.currentSubmission,
  rejectionCategories: (state) => state.rejectionCategories,
  auditLog: (state) => state.auditLog,
  auditLogPagination: (state) => state.auditLogPagination,
  availableProducts: (state) => state.availableProducts,
  availableCountries: (state) => state.availableCountries,
  isLoading: (state) => state.isLoading,
  error: (state) => state.error,
};

const mutations = {
  SET_SUBMISSIONS(state, data) {
    state.submissions = data;
  },
  SET_SUMMARY(state, data) {
    state.summary = data;
  },
  SET_PAGINATION(state, data) {
    state.pagination = data;
  },
  SET_CURRENT_SUBMISSION(state, data) {
    state.currentSubmission = data;
  },
  SET_REJECTION_CATEGORIES(state, data) {
    state.rejectionCategories = data;
  },
  SET_AUDIT_LOG(state, data) {
    state.auditLog = data;
  },
  SET_AUDIT_LOG_PAGINATION(state, data) {
    state.auditLogPagination = data;
  },
  SET_AVAILABLE_PRODUCTS(state, data) {
    state.availableProducts = data;
  },
  SET_AVAILABLE_COUNTRIES(state, data) {
    state.availableCountries = data;
  },
  SET_LOADING(state, val) {
    state.isLoading = val;
  },
  SET_ERROR(state, val) {
    state.error = val;
  },
};

const actions = {
  async fetchSubmissions({ commit }, params = {}) {
    ArchBaseService.init();
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const response = await ArchBaseService.get(`${API_BASE}/submissions`, params);
      const { submissions, pagination, summary } = response.data.details;
      commit("SET_SUBMISSIONS", submissions);
      commit("SET_PAGINATION", pagination);
      commit("SET_SUMMARY", summary);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSubmissionDetail({ commit }, submissionId) {
    ArchBaseService.init();
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const response = await ArchBaseService.get(`${API_BASE}/submissions/${submissionId}`);
      commit("SET_CURRENT_SUBMISSION", response.data.details);
      return response.data.details;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async assignReviewer({ commit, state }, { submissionId, assignedTo }) {
    ArchBaseService.init();
    try {
      const response = await ArchBaseService.put(
        `${API_BASE}/submissions/${submissionId}/assign`,
        { assigned_to: assignedTo }
      );
      if (state.currentSubmission) {
        const detail = JSON.parse(JSON.stringify(state.currentSubmission));
        detail.submission.assigned_to = response.data.details.assigned_to;
        detail.submission.status = response.data.details.status;
        commit("SET_CURRENT_SUBMISSION", detail);
      }
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async reviewSection({ commit, state }, { sectionId, decision, rejectionCategoryId, reason }) {
    ArchBaseService.init();
    try {
      const body = { decision };
      if (decision === "rejected") {
        body.rejection_category_id = rejectionCategoryId;
        if (reason) body.reason = reason;
      }
      const response = await ArchBaseService.put(
        `${API_BASE}/sections/${sectionId}/review`,
        body
      );
      if (state.currentSubmission) {
        const detail = JSON.parse(JSON.stringify(state.currentSubmission));
        const section = detail.sections.find((s) => s.id === sectionId);
        if (section) {
          section.status = response.data.details.status;
          section.rejection_reason = response.data.details.rejection_reason;
          section.rejection_category_id = response.data.details.rejection_category_id;
        }
        commit("SET_CURRENT_SUBMISSION", detail);
      }
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async reviewDocument({ commit, state }, { documentId, decision, rejectionCategoryId, reason }) {
    ArchBaseService.init();
    try {
      const body = { decision };
      if (decision === "rejected") {
        body.rejection_category_id = rejectionCategoryId;
        if (reason) body.reason = reason;
      }
      const response = await ArchBaseService.put(
        `${API_BASE}/documents/${documentId}/review`,
        body
      );
      if (state.currentSubmission) {
        const detail = JSON.parse(JSON.stringify(state.currentSubmission));
        const result = response.data.details;
        const updateDoc = (file) => {
          if (file.id === documentId) {
            file.status = result.status;
            file.rejection_reason = result.rejection_reason;
            file.rejection_category_id = result.rejection_category_id;
            file.reviewed_by = result.reviewed_by;
            file.reviewed_at = result.reviewed_at;
          }
        };
        detail.documents.business_documents.forEach((d) => d.uploaded.forEach(updateDoc));
        detail.documents.stakeholder_documents.forEach((sh) =>
          sh.documents.forEach((d) => d.uploaded.forEach(updateDoc))
        );
        commit("SET_CURRENT_SUBMISSION", detail);
      }
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async submitTier1Decision({ commit, state }, { submissionId, decision, reason }) {
    ArchBaseService.init();
    try {
      const body = { tier: 1, decision };
      if (reason) body.reason = reason;
      const response = await ArchBaseService.post(
        `${API_BASE}/submissions/${submissionId}/decision`,
        body
      );
      if (state.currentSubmission) {
        const detail = JSON.parse(JSON.stringify(state.currentSubmission));
        detail.submission.status = response.data.details.status;
        detail.submission.tier1_status = response.data.details.decision;
        commit("SET_CURRENT_SUBMISSION", detail);
      }
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async submitTier2Decision({ commit, state }, { submissionId, decision, reason, approvedProducts, approvedCountries, volumeLimits }) {
    ArchBaseService.init();
    try {
      const body = { decision };
      if (reason) body.reason = reason;
      if (approvedProducts) body.approved_products = approvedProducts;
      if (approvedCountries) body.approved_countries = approvedCountries;
      if (volumeLimits) body.volume_limits = volumeLimits;
      const response = await ArchBaseService.post(
        `${API_BASE}/submissions/${submissionId}/tier2-decision`,
        body
      );
      if (state.currentSubmission) {
        const detail = JSON.parse(JSON.stringify(state.currentSubmission));
        detail.submission.status = response.data.details.status;
        detail.submission.tier2_status = response.data.details.decision;
        if (response.data.details.approval_tier) {
          detail.approval_tier = response.data.details.approval_tier;
        }
        commit("SET_CURRENT_SUBMISSION", detail);
      }
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async fetchAvailableProducts({ commit }, submissionId) {
    ArchBaseService.init();
    try {
      const response = await ArchBaseService.get(
        `${API_BASE}/submissions/${submissionId}/available-products`
      );
      commit("SET_AVAILABLE_PRODUCTS", response.data.details || []);
      return response.data.details;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async fetchAvailableCountries({ commit }, submissionId) {
    ArchBaseService.init();
    try {
      const response = await ArchBaseService.get(
        `${API_BASE}/submissions/${submissionId}/available-countries`
      );
      commit("SET_AVAILABLE_COUNTRIES", response.data.details || []);
      return response.data.details;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async fetchRejectionCategories({ commit }) {
    ArchBaseService.init();
    try {
      const response = await ArchBaseService.get(`${API_BASE}/rejection-categories`);
      const categories = (response.data.details || []).map(
        (item) => item.dataValues || item
      );
      commit("SET_REJECTION_CATEGORIES", categories);
      return categories;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },

  async fetchAuditLog({ commit }, { submissionId, page = 1, limit = 50 }) {
    ArchBaseService.init();
    try {
      const response = await ArchBaseService.get(
        `${API_BASE}/submissions/${submissionId}/audit-log`,
        { page, limit }
      );
      commit("SET_AUDIT_LOG", response.data.details.items);
      commit("SET_AUDIT_LOG_PAGINATION", response.data.details.pagination);
      return response.data.details;
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      commit("SET_ERROR", msg);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
